import type {
  Map,
  MapGeoJSONFeature,
  MapMouseEvent,
  MapTouchEvent,
} from "maplibre-gl";
import {
  renderedFeatureLookupTarget,
  selectRenderedFeature,
} from "../../../lib/geo/maplibreFeature.ts";
import type { LngLat } from "../../../lib/geo/types.ts";
import { debugSelection } from "../../../lib/debug.ts";
import type { OsmSelectionTarget } from "../selection/osmLookup.ts";

// This module handles selection and the map-surface context-menu gesture.
// Desktop right-clicks arrive as MapLibre `contextmenu` events. On touch
// devices, a long press is emulated from MapLibre `touchstart`/`touchmove`
// events and then follows the same context-menu path.
//
// The touch fallback is needed because mobile browsers do not consistently
// expose a usable `contextmenu` event from a MapLibre canvas; MapLibre may
// consume the browser's native long-press behavior while handling the map.
// Both input paths ultimately dispatch a synthetic event to the Bits UI
// adapter, which supplies the menu's positioning and accessibility behavior.

interface MapSelectionGestureHandlers {
  onSelect: (
    selectionTarget: OsmSelectionTarget,
    renderedFeature?: MapGeoJSONFeature,
  ) => void | Promise<void>;
  onClear: () => void;
  onContextMenuTarget?: (lngLat: LngLat) => void;
  contextMenuTrigger?: HTMLElement | null;
}

const TOUCH_LONG_PRESS_MS = 500;
const TOUCH_LONG_PRESS_MOVE_TOLERANCE_PX = 8;
// Releasing a touch long press can emit a click, which would otherwise clear
// or replace the coordinate stored for the context menu.
const TOUCH_LONG_PRESS_CLICK_SUPPRESSION_MS = 1_000;

export function attachMapSelectionGestures(
  map: Map,
  handlers: MapSelectionGestureHandlers,
): {
  destroy: () => void;
} {
  let touchLongPress:
    | {
      clientX: number;
      clientY: number;
      lngLat: LngLat;
      timeoutId: ReturnType<typeof globalThis.setTimeout>;
    }
    | undefined;
  let suppressClicksUntil = 0;
  const contextMenuTrigger = handlers.contextMenuTrigger;

  function isMapCanvasEvent(event: Event): boolean {
    return event.target === map.getCanvas();
  }

  function handleClick(e: MapMouseEvent): void {
    if (!isMapCanvasEvent(e.originalEvent)) return;
    if (Date.now() <= suppressClicksUntil) return;

    const clickLngLat: LngLat = [e.lngLat.lng, e.lngLat.lat];
    const features = map.queryRenderedFeatures(e.point);
    const selectedFeature = selectRenderedFeature(features);
    if (!selectedFeature) {
      handlers.onClear();
      return;
    }
    const renderedTarget = renderedFeatureLookupTarget(
      selectedFeature,
      clickLngLat,
    );
    const lookupTarget: OsmSelectionTarget = {
      kind: "nearby",
      ...renderedTarget,
    };

    debugSelection("click selected feature", () => ({
      point: e.point,
      clickLngLat: { lng: clickLngLat[0], lat: clickLngLat[1] },
      lookupLngLat: {
        lng: lookupTarget.lngLat[0],
        lat: lookupTarget.lngLat[1],
      },
      nameHint: lookupTarget.nameHint,
    }));

    handlers.onSelect(lookupTarget, selectedFeature);
  }

  function openContextMenu(
    lngLat: LngLat,
    clientX: number,
    clientY: number,
  ): void {
    handlers.onContextMenuTarget?.(lngLat);
    // MapLibre owns gesture detection; this synthetic event hands the result
    // to Bits UI, which provides positioning, focus management, and dismissal.
    contextMenuTrigger?.dispatchEvent(
      new MouseEvent("contextmenu", {
        bubbles: true,
        cancelable: true,
        clientX,
        clientY,
        button: 2,
        buttons: 2,
        view: globalThis as unknown as Window,
      }),
    );
  }

  function cancelTouchLongPress(): void {
    if (!touchLongPress) return;
    globalThis.clearTimeout(touchLongPress.timeoutId);
    touchLongPress = undefined;
  }

  function handleMapContextMenu(e: MapMouseEvent): void {
    if (!isMapCanvasEvent(e.originalEvent)) return;
    e.originalEvent.preventDefault();
    openContextMenu(
      [e.lngLat.lng, e.lngLat.lat],
      e.originalEvent.clientX,
      e.originalEvent.clientY,
    );
  }

  function handleTouchStart(e: MapTouchEvent): void {
    if (!isMapCanvasEvent(e.originalEvent)) return;
    if (e.originalEvent.touches.length !== 1) {
      cancelTouchLongPress();
      return;
    }

    cancelTouchLongPress();
    const touch = e.originalEvent.touches[0];
    if (!touch) return;
    const lngLat: LngLat = [e.lngLat.lng, e.lngLat.lat];
    const timeoutId = globalThis.setTimeout(() => {
      if (!touchLongPress || touchLongPress.timeoutId !== timeoutId) return;

      const gesture = touchLongPress;
      touchLongPress = undefined;
      suppressClicksUntil = Date.now() +
        TOUCH_LONG_PRESS_CLICK_SUPPRESSION_MS;
      openContextMenu(gesture.lngLat, gesture.clientX, gesture.clientY);
    }, TOUCH_LONG_PRESS_MS);
    touchLongPress = {
      clientX: touch.clientX,
      clientY: touch.clientY,
      lngLat,
      timeoutId,
    };
  }

  function handleTouchMove(e: MapTouchEvent): void {
    if (!touchLongPress) return;
    if (e.originalEvent.touches.length !== 1) {
      cancelTouchLongPress();
      return;
    }

    const touch = e.originalEvent.touches[0];
    if (!touch) {
      cancelTouchLongPress();
      return;
    }
    if (
      Math.hypot(
        touch.clientX - touchLongPress.clientX,
        touch.clientY - touchLongPress.clientY,
      ) > TOUCH_LONG_PRESS_MOVE_TOLERANCE_PX
    ) {
      cancelTouchLongPress();
    }
  }

  function handleTouchEnd(): void {
    cancelTouchLongPress();
  }

  function handleWindowBlur(): void {
    cancelTouchLongPress();
  }

  function handleVisibilityChange(): void {
    if (globalThis.document?.visibilityState === "hidden") {
      cancelTouchLongPress();
    }
  }

  function destroy(): void {
    map.off("click", handleClick);
    map.off("contextmenu", handleMapContextMenu);
    map.off("touchstart", handleTouchStart);
    map.off("touchmove", handleTouchMove);
    map.off("touchend", handleTouchEnd);
    map.off("touchcancel", handleTouchEnd);
    globalThis.removeEventListener("blur", handleWindowBlur);
    globalThis.document?.removeEventListener(
      "visibilitychange",
      handleVisibilityChange,
    );
    cancelTouchLongPress();
  }

  map.on("click", handleClick);
  map.on("contextmenu", handleMapContextMenu);
  map.on("touchstart", handleTouchStart);
  map.on("touchmove", handleTouchMove);
  map.on("touchend", handleTouchEnd);
  map.on("touchcancel", handleTouchEnd);
  globalThis.addEventListener("blur", handleWindowBlur);
  globalThis.document?.addEventListener(
    "visibilitychange",
    handleVisibilityChange,
  );

  return { destroy };
}
